/**
 * 4 层大底池可视化组件
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Copy, Download, Star, ChevronDown, ChevronUp, Cloud, CloudOff, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useFavoritesStore } from '../stores/favorites.js';

function copyText(text) {
  if (navigator.clipboard) {
    return navigator.clipboard.writeText(text).then(() => true).catch(() => false);
  }
  const ta = document.createElement('textarea');
  ta.value = text; document.body.appendChild(ta); ta.select();
  try { document.execCommand('copy'); } catch {}
  document.body.removeChild(ta);
  return true;
}

export function PoolCard({ title, list, expected, desc, stats, isMain, innerCodes = new Set(), level, onFavChange }) {
  const addFavStore = useFavoritesStore(s => s.add);
  const [showAll, setShowAll] = useState(false);
  const displayItems = showAll ? list : list.slice(0, 200);
  const isL1 = title.includes('1000');

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={clsx(
        'card card-hover transition-all',
        isMain && 'lg:col-span-3'
      )}
    >
      <div className="flex items-start justify-between gap-2 sm:gap-3 mb-2 sm:mb-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm sm:text-lg font-bold flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <span>{title.split(' ')[0]}</span>
            <span className="gradient-text">{title.split(' ')[1]}</span>
            <span className="hidden sm:inline">{title.split(' ').slice(2).join(' ')}</span>
            {isMain && (
              <span className="text-[9px] sm:text-[10px] px-1.5 sm:px-2 py-0.5 rounded-md bg-amber-500/20 text-amber-400 border border-amber-500/30 font-bold">
                🐝 v3
              </span>
            )}
          </div>
          <div className="text-[10px] sm:text-xs text-ink-3 mt-0.5 sm:mt-1 line-clamp-1">{desc}</div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-xl sm:text-2xl font-bold gradient-text">{list.length}</div>
          <div className="text-[9px] sm:text-[10px] text-ink-3">候选 {stats?.raw || '-'}</div>
        </div>
      </div>

      <div className="flex gap-1.5 sm:gap-2 mb-2 sm:mb-3 flex-wrap">
        <button
          onClick={async () => {
            const text = list.map(x => x.code).join(' ');
            const ok = await copyText(text);
            toast[ok ? 'success' : 'error'](ok ? `已复制 ${list.length} 注` : '复制失败');
          }}
          className="btn-ghost flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs px-2 sm:px-3 py-1 sm:py-2"
        >
          <Copy size={11} /> 复制
        </button>
        <button
          onClick={() => {
            const text = list.map(x => x.code).join(' ');
            const blob = new Blob([text], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `3d_${title.split(' ')[0]}_pool.txt`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success('已导出');
          }}
          className="btn-ghost flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs px-2 sm:px-3 py-1 sm:py-2"
        >
          <Download size={11} /> 导出
        </button>
        <button
          onClick={() => {
            const user = (() => { try { return JSON.parse(localStorage.getItem('fucai3d_user') || 'null'); } catch (e) { return null; } })();
            const favData = {
              time: new Date().toISOString(),
              title,
              count: list.length,
              codes: list.map(x => x.code).join(' '),
              level: level || null,
              source: 'pool',
            };
            addFavStore(favData);
            if (user?.id) {
              toast.success('已收藏 · 跨设备同步中...', { icon: '☁️' });
            } else {
              toast.success('已收藏到本地', { icon: '⭐' });
            }
            onFavChange?.();
          }}
          className="btn-ghost flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs px-2 sm:px-3 py-1 sm:py-2"
        >
          <Star size={11} /> 收藏
        </button>
      </div>

      <div className="grid grid-cols-[repeat(auto-fill,minmax(40px,1fr))] sm:grid-cols-[repeat(auto-fill,minmax(54px,1fr))] gap-1 sm:gap-1.5 max-h-[360px] sm:max-h-[420px] overflow-y-auto pr-1">
        {displayItems.map((item, i) => {
          const isHit = innerCodes.has(item.code);
          const isDan = item.dans && item.dans.length > 0;
          const isHot = item.hots && item.hots.length > 0;
          return (
            <motion.div
              key={item.code}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: Math.min(i * 0.003, 0.5) }}
              className={clsx(
                'num-ball cursor-pointer text-[10px] sm:text-xs hover:scale-110 transition-transform',
                isHit && 'num-ball-hit',
                isDan && !isHit && 'num-ball-dan',
                isHot && !isHit && !isDan && 'num-ball-hot',
              )}
              title={item.tags}
            >
              {item.code}
            </motion.div>
          );
        })}
      </div>

      {list.length > 200 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-3 w-full py-2 rounded-lg bg-white/5 border border-white/10 text-xs text-ink-2 hover:bg-white/10 transition flex items-center justify-center gap-1.5"
        >
          {showAll ? (
            <><ChevronUp size={12} /> 收起(显示前 200 注)</>
          ) : (
            <><ChevronDown size={12} /> 展开全部 {list.length} 注</>
          )}
        </button>
      )}
    </motion.div>
  );
}

export function PoolsView({ pools }) {
  const { pool1000, pool500, pool300, pool100, stats } = pools;
  const inner500 = new Set(pool500.map(x => x.code));
  const inner300 = new Set(pool300.map(x => x.code));
  const inner100 = new Set(pool100.map(x => x.code));

  // 跨设备同步收藏
  const addFav = useFavoritesStore(s => s.add);
  const fetchFromServer = useFavoritesStore(s => s.fetchFromServer);
  const lastSyncedAt = useFavoritesStore(s => s.lastSyncedAt);
  const syncing = useFavoritesStore(s => s.syncing);
  const user = (() => { try { return JSON.parse(localStorage.getItem('fucai3d_user') || 'null'); } catch (e) { return null; } })();

  useEffect(() => {
    if (user?.id) fetchFromServer();
  }, [user?.id]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
      <PoolCard
        title="L1 1000 注基础池"
        list={pool1000}
        desc="仅过滤用户手动杀号 · 推荐投注取前 600 注"
        stats={{ raw: stats.L1_raw }}
        innerCodes={inner500}
        isMain
        level="L1"
      />
      <PoolCard
        title="L2 500 注底池"
        list={pool500}
        desc="L1 + ML 概率排序(15 维特征)"
        stats={{ raw: stats.L2_raw }}
        innerCodes={inner300}
        level="L2"
      />
      <PoolCard
        title="L3 300 注底池"
        list={pool300}
        desc="L2 + 走势加权(重号+胆码+跨度反转)"
        stats={{ raw: stats.L3_raw }}
        innerCodes={inner100}
        level="L3"
      />
      <PoolCard
        title="L4 100 注底池"
        list={pool100}
        desc="🐝 v3 + XGBoost 60 棵 23 维特征(21% 命中,+9.2% ROI)"
        stats={{ raw: stats.L4_raw }}
        innerCodes={new Set()}
        level="L4"
      />
    </div>
  );
}
